import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  CircularProgress,
  Typography,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import generalApi from "../../api/generalApi";
import { toast } from "react-toastify";
import { Code, Eye } from "lucide-react";

const ItemEditorModal = ({ open, onClose, projectId, item, onItemUpdated }) => {
  const [name, setName] = useState("");
  const [content, setContent] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [editorView, setEditorView] = useState("visual");

  // Initialize form with item data when opened
  useEffect(() => {
    if (open && item) {
      setName(item.name || "");
      setContent(item.content || "");
      setError(null);
    }
  }, [open, item]);

  const handleSubmit = async () => {
    if (!name.trim()) {
      setError("Name is required");
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const updatedItem = {
        name: name.trim(),
        content: content,
      };

      await generalApi.project.updateProjectItem(projectId, item._id, updatedItem);
      
      onItemUpdated({
        ...item,
        ...updatedItem,
        updatedAt: new Date(),
      });
      
      toast.success("Item updated successfully!");
      onClose();
    } catch (err) {
      console.error("Error updating item:", err);
      setError("Failed to update item. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleViewChange = (event, newView) => {
    if (newView !== null) {
      setEditorView(newView);
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={!loading ? onClose : undefined} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '12px',
          boxShadow: '0 8px 32px -4px rgba(0, 0, 0, 0.2)',
          backgroundImage: 'none',
          bgcolor: '#1a1a1a'
        }
      }}
    >
      <DialogTitle 
        sx={{ 
          px: 3,
          pt: 3,
          pb: 2,
          fontSize: '1.5rem',
          fontWeight: 600,
          color: 'white',
          borderBottom: '1px solid',
          borderColor: 'rgba(255, 255, 255, 0.1)'
        }}
      >
        Edit Item
      </DialogTitle>
      
      <DialogContent sx={{ p: 3 }}>
        <Box sx={{ mb: 3 }}>
          <Typography 
            variant="subtitle1" 
            sx={{ 
              mb: 1,
              fontWeight: 500,
              color: 'rgba(255, 255, 255, 0.9)'
            }}
          >
            Item Name
          </Typography>
          <TextField
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            required
            error={!name.trim() && !!error}
            helperText={!name.trim() && error ? "Name is required" : ""}
            disabled={loading}
            placeholder="Enter item name"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
                backgroundColor: '#2a2a2a',
                color: 'white',
                '&:hover': {
                  backgroundColor: '#2d2d2d',
                },
                '&.Mui-focused': {
                  backgroundColor: '#2d2d2d',
                  '& fieldset': {
                    borderColor: '#be58da',
                    borderWidth: '2px',
                  },
                  boxShadow: '0 0 0 4px rgba(190, 88, 218, 0.15)'
                },
                '& fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.15)'
                },
                '& input': {
                  color: 'white',
                  '&::placeholder': {
                    color: 'rgba(255, 255, 255, 0.5)',
                    opacity: 1
                  }
                }
              }
            }}
          />
        </Box>

        <Box sx={{ mb: 2 }}>
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 1
          }}>
            <Typography 
              variant="subtitle1" 
              sx={{ 
                fontWeight: 500,
                color: 'rgba(255, 255, 255, 0.9)'
              }}
            >
              Content
            </Typography>
            <ToggleButtonGroup
              value={editorView}
              exclusive
              onChange={handleViewChange}
              size="small"
              sx={{
                bgcolor: '#2a2a2a',
                '& .MuiToggleButton-root': {
                  border: '1px solid',
                  borderColor: 'rgba(255, 255, 255, 0.15)',
                  color: 'rgba(255, 255, 255, 0.7)',
                  px: 2,
                  py: 0.75,
                  '&.Mui-selected': {
                    bgcolor: '#be58da',
                    color: 'white',
                    '&:hover': {
                      bgcolor: '#a94dc3',
                    },
                    '& .icon': {
                      color: 'white'
                    }
                  },
                  '&:hover': {
                    bgcolor: 'rgba(190, 88, 218, 0.15)'
                  }
                }
              }}
            >
              <ToggleButton 
                value="visual"
                sx={{ 
                  gap: 1,
                  textTransform: 'none',
                  fontWeight: 500,
                  borderTopLeftRadius: '6px !important',
                  borderBottomLeftRadius: '6px !important'
                }}
              >
                <Eye size={16} className="icon" />
                Visual
              </ToggleButton>
              <ToggleButton 
                value="code"
                sx={{ 
                  gap: 1,
                  textTransform: 'none',
                  fontWeight: 500,
                  borderTopRightRadius: '6px !important',
                  borderBottomRightRadius: '6px !important'
                }}
              >
                <Code size={16} className="icon" />
                Code
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
          <Box 
            sx={{ 
              border: '1px solid',
              borderColor: 'rgba(255, 255, 255, 0.15)',
              borderRadius: '8px',
              overflow: 'hidden',
              bgcolor: '#2a2a2a',
              boxShadow: '0 2px 8px -2px rgba(0, 0, 0, 0.3)',
              '& .ql-toolbar': {
                borderTop: 'none',
                borderLeft: 'none',
                borderRight: 'none',
                borderBottom: '1px solid',
                borderColor: 'rgba(255, 255, 255, 0.15)',
                backgroundColor: '#232323',
                '& .ql-stroke': {
                  stroke: 'rgba(255, 255, 255, 0.7)'
                },
                '& .ql-fill': {
                  fill: 'rgba(255, 255, 255, 0.7)'
                },
                '& .ql-picker': {
                  color: 'rgba(255, 255, 255, 0.7)'
                },
                '& .ql-picker-options': {
                  backgroundColor: '#2a2a2a',
                  border: '1px solid rgba(255, 255, 255, 0.15)'
                }
              },
              '& .ql-container': {
                border: 'none',
                backgroundColor: '#2a2a2a',
                minHeight: '300px',
                color: 'white'
              },
              '& .ql-editor': {
                minHeight: '300px',
                fontSize: '1rem',
                lineHeight: 1.6,
                padding: '16px',
                color: 'white',
                '&.ql-blank::before': {
                  color: 'rgba(255, 255, 255, 0.5)',
                  fontStyle: 'normal',
                  opacity: 0.7
                }
              }
            }}
          >
            {editorView === 'visual' ? (
              <ReactQuill
                theme="snow"
                value={content}
                onChange={setContent}
                placeholder="Enter your content here..."
                modules={{
                  toolbar: [
                    [{ 'header': [1, 2, 3, false] }],
                    ['bold', 'italic', 'underline', 'strike', 'blockquote'],
                    [{'list': 'ordered'}, {'list': 'bullet'}, {'indent': '-1'}, {'indent': '+1'}],
                    ['link', 'image'],
                    ['clean']
                  ],
                }}
                formats={[
                  'header',
                  'bold', 'italic', 'underline', 'strike', 'blockquote',
                  'list', 'bullet', 'indent',
                  'link', 'image'
                ]}
              />
            ) : (
              <TextField
                multiline
                fullWidth
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="Enter HTML content here..."
                sx={{
                  '& .MuiOutlinedInput-root': {
                    padding: 0,
                    '& fieldset': {
                      border: 'none'
                    }
                  },
                  '& .MuiOutlinedInput-input': {
                    fontFamily: 'monospace',
                    padding: 2,
                    minHeight: '300px',
                    fontSize: '0.875rem',
                    lineHeight: 1.6,
                    color: 'white',
                    backgroundColor: '#2a2a2a',
                    '&::placeholder': {
                      color: 'rgba(255, 255, 255, 0.5)',
                      opacity: 1
                    }
                  }
                }}
              />
            )}
          </Box>
        </Box>
        
        {error && !error.includes("name") && (
          <Typography 
            color="error" 
            variant="body2" 
            sx={{ 
              mt: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}
          >
            {error}
          </Typography>
        )}
      </DialogContent>
      
      <DialogActions 
        sx={{ 
          px: 3, 
          py: 2.5,
          borderTop: '1px solid',
          borderColor: 'rgba(255, 255, 255, 0.1)',
          gap: 1
        }}
      >
        <Button 
          onClick={onClose} 
          disabled={loading}
          sx={{
            minWidth: '100px',
            borderRadius: '8px',
            textTransform: 'none',
            fontWeight: 500,
            color: 'rgba(255, 255, 255, 0.7)',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.1)'
            }
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || !name.trim()}
          sx={{
            minWidth: '100px',
            bgcolor: "#9c27b0",
            "&:hover": { bgcolor: "#7b1fa2" },
            borderRadius: '8px',
            textTransform: 'none',
            fontWeight: 500,
            boxShadow: 'none',
            '&:hover': {
              boxShadow: '0 4px 12px -2px rgba(156, 39, 176, 0.3)',
              bgcolor: "#7b1fa2"
            }
          }}
        >
          {loading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={16} thickness={6} sx={{ color: 'white' }} />
              <span>Saving...</span>
            </Box>
          ) : (
            "Save Changes"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ItemEditorModal; 